import { eElasticEvent, ePerformanceMeasure } from '../../utils/enums';

import { API_TIMEOUT } from '../../utils/constants';
import Global from '../../global';
import PerformanceFacade from '../../utils/performance/performance-facade';
import { getLogDataFromUserContext } from '../clickstream-service/utils';
import { getWidgetsAPI } from '../../config/kambi/api-config/kambi-config';
import htmlHelper from '../../utils/html-helper';

const callbackQueue = [];

export const addKambiWidgetsReadyCallbacks = callback => {
  if (callback) callbackQueue.push(callback);
};

class KambiWidgets {
  WAPI = null;

  init = (callbacks, useProdApis, userContext) => {
    PerformanceFacade.sendDurationReport(
      eElasticEvent.KambiWidgetApiScriptStart,
      getLogDataFromUserContext(userContext),
      ePerformanceMeasure.KambiWidgetApiScriptStart
    );

    Global.logger.log('Initializing kambi-widget-api script');

    const widgetsApi = document.createElement('script');
    const { url: widgetApiUrl } = getWidgetsAPI(useProdApis);
    widgetsApi.onload = () => this.widgetReady(callbacks, userContext);
    widgetsApi.src = `${widgetApiUrl}/kambi-widget-api.js?cb=${new Date().setHours(
      0,
      0,
      0,
      0
    )}`;
    widgetsApi.setAttribute('data-ot-ignore', '');
    htmlHelper.appendScriptsToNode('kambiWidgetApi', [widgetsApi]);
  };

  /**
   *
   * @param {KambiCallbacks} callbacks
   */
  widgetReady = (callbacks, userContext) => {
    PerformanceFacade.sendDurationReport(
      eElasticEvent.KambiWidgetApiScriptEnd,
      getLogDataFromUserContext(userContext),
      ePerformanceMeasure.KambiWidgetApiScriptEnd
    );
    Global.logger.log('Kambi-widget-api script loaded');

    window.KambiWidget.ready.then(wapi => {
      PerformanceFacade.sendDurationReport(
        eElasticEvent.KambiWidgetApiScriptReady,
        getLogDataFromUserContext(userContext),
        ePerformanceMeasure.KambiWidgetApiScriptReady
      );

      Global.logger.log('Kambi-widget-api is ready');

      callbacks.navigateClient();

      wapi.subscribe((response, WAPI) => {
        switch (response.type) {
          case WAPI.NAVIGATE:
            callbacks.navigateClient();
            break;
          case WAPI.PLACE_BET_STATE:
            callbacks.getBetStatus(response.data);
            break;
          case WAPI.BETSLIP_OUTCOMES:
            callbacks.getCurrentBetSlipOutcomes(response.data);
            break;
          case WAPI.CLIENT_ODDS_FORMAT:
            callbacks.clientOddsFormatChanged(response.data);
            break;
          case WAPI.USER_SESSION_CHANGE:
            Global.logger.log('Received user session change', response.data);
            break;
          case WAPI.LOGOUT:
            Global.logger.log('Received logout', response.data);
            break;
          default:
            Global.logger.warn(
              `[KambiWidgets] Missed case => ${response.type}`,
              response.data
            );
        }
      });

      Global.logger.info('[KambiWidgets] KambiWidget ready to use');
      this.WAPI = wapi;
      window.WAPI = wapi;
      this.sendKambiWidgetEvent(wapi.BETSLIP_OUTCOMES, wapi.CLIENT_ODDS_FORMAT);
      callbacks.clearQueuedOutcomes();

      const event = new Event('kambi-widget-api-ready');
      window.dispatchEvent(event);

      while (callbackQueue.length) {
        const callback = callbackQueue.shift();
        callback();
      }
    });
  };

  constructor(callbacks, useProdApis, userContext) {
    this.init(callbacks, useProdApis, userContext);
  }

  setOutcomesToBetSlip = function(outcomeIds, updateMode, couponType) {
    this.WAPI.set(this.WAPI.BETSLIP_OUTCOMES, {
      updateMode: this.WAPI.BETSLIP_OUTCOMES_ARGS[updateMode],
      outcomes: outcomeIds,
      couponType: this.WAPI.BETSLIP_OUTCOMES_ARGS[couponType]
    });
  };

  loginAsync = function(userContext, token) {
    return new Promise((resolve, reject) => {
      let timeout;
      const callbacks = [
        reason => {
          this._hideBetslipClientMode();

          reject(reason);
          clearTimeout(timeout);
        },
        sessionId => {
          if (!this._hideBetslipClientMode()) {
            this.showBetslip(true);
          }
          clearTimeout(timeout);
          resolve({ isLoggedIn: !!sessionId, sessionId });
        }
      ];

      if (this.WAPI) {
        timeout = setTimeout(() => {
          reject('TIMEOUT');
        }, API_TIMEOUT);

        this._login(userContext, token, ...callbacks);
      } else {
        addKambiWidgetsReadyCallbacks(() => {
          timeout = setTimeout(() => reject('TIMEOUT'), API_TIMEOUT);

          this._login(userContext, token, ...callbacks);
        });
      }
    });
  };

  _login = (userContext, token, loginFailedCallback, userDataCallback) => {
    const { locale, market, currency } = userContext;
    if (this.WAPI) {
      this.WAPI.request(this.WAPI.LOGIN, {
        punterId: '',
        ticket: token,
        currency,
        market,
        locale
      });

      const subscription = this.WAPI.subscribe((response, WAPI) => {
        // eslint-disable-next-line no-undef
        switch (response.type) {
          case WAPI.USER_SESSION_CHANGE: {
            const { logoutReason } = response.data;
            if (logoutReason === 'LOGIN_FAILED') {
              if (typeof loginFailedCallback === 'function') {
                loginFailedCallback(logoutReason);
              }
              subscription.unsubscribe();
            }
            break;
          }
          case WAPI.USER_DATA: {
            const { sessionId } = response.data;
            if (sessionId) {
              if (typeof userDataCallback === 'function') {
                userDataCallback(sessionId);
              }

              subscription.unsubscribe();
            }

            break;
          }
        }
      });

      this.WAPI.request(this.WAPI.USER_DATA);
      this.WAPI.request(this.WAPI.USER_SESSION_CHANGE);
    }
  };

  logout = function() {
    if (this.WAPI) this.WAPI.request(this.WAPI.LOGOUT);
  };

  navigateClient = url => {
    if (url) this.WAPI.navigateClient(url);
  };

  removeOutcomesFromBetSlip = function(outcomesIds) {
    this.WAPI.set(this.WAPI.BETSLIP_OUTCOMES_REMOVE, {
      outcomes: outcomesIds
    });
  };

  sendKambiWidgetEvent = (betslipOutcomes, clientOddsFormat) => {
    this.WAPI.request(betslipOutcomes);
    this.WAPI.request(clientOddsFormat);
  };

  showBetslip = function(show) {
    this.WAPI.set(show ? this.WAPI.BETSLIP_SHOW : this.WAPI.BETSLIP_HIDE);
  };

  _hideBetslipClientMode = function() {
    const isClientMode =
      new URL(window.location.href).searchParams.get('clientMode') === 'true';

    if (isClientMode) this.showBetslip(false);

    return isClientMode;
  };
}

export default KambiWidgets;
